// Treasury → Revenue → Commission policies (SM-388).
//
// ── one page, the whole resolution chain ─────────────────────────────
//
// QA's finding was that two nav entries both said "commission" and
// neither did one job: the Commissions REPORT carried a rate editor, and
// this page — the one named for policy — could only edit the bottom
// level of a three-level chain. The split is now the obvious one:
// Commissions reports what was paid, and this page decides what will be,
// at every level that can decide it.
//
//   Markets  — an override here outranks everything below it.
//   Events   — covers child markets that have no override of their own.
//   Admins   — the author's default, the bottom of the chain, and the
//              only level with three independent rates (SM-387).
//
// The tab lives in the URL so a policy screen can be linked in a ticket
// and land where the author meant.
//
// ── and every rate names its currency (SM-497) ───────────────────────
//
// A commission rate is a share of a fee the platform already collected,
// so its currency is never chosen here — it is the currency the book
// collected in, which makes "20%" and "20% of Gold" the same fact only
// while there is one currency. Two filters narrow the page by book and
// by currency, both in the URL beside the tab, and both are ANDed
// rather than made to drive each other: a pair that selects nothing
// (Gold on the Game book) is a real answer worth stating.
//
// ── the admin defaults ARE per currency now (SM-522) ─────────────────
//
// This page used to say the opposite, and had to: there was one triple
// per admin, applied to whichever book published their poll, so the
// currency filter deliberately did not narrow the admin list and a panel
// above it explained why. An admin now has one policy per currency —
// separate rates, separate version, separate audit history — so the
// filter narrows these rows like every other list on the page, and the
// panel says where each currency is earned instead of apologising for a
// configuration that did not exist.
//
// Under no currency filter an admin contributes THREE rows. That is the
// honest shape: "what does this person earn" has three answers, and
// after the migration most admins are configured in Gold and unset in
// Red and Green — which is precisely the decision an operator is here to
// make.
//
// ── grouping (admins) is by decision, not by rate ────────────────────
//
// The two admin groups are "needs a decision" and "configured", because
// that is the only division that tells an operator where to spend
// attention. Grouping by rate would put an admin deliberately set to 0%
// next to one nobody has ever considered, which is precisely the
// distinction SM-365 established the "Not set" state to preserve.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { LoadFailed } from "@/components/fees/LoadFailed";
import {
  AdjustCommissionDialog,
  CommissionNotice,
} from "@/components/commission/AdjustCommissionDialog";
import {
  CommissionRateTriple,
  FEE_TYPE_LABEL,
  FEE_TYPE_ORDER,
} from "@/components/commission/CommissionRateTriple";
import {
  EventPolicies,
  MarketPolicies,
} from "@/components/commission/OverridePolicies";
import {
  buildRateRows,
  filterRows,
  rateRowKey,
  type RateRow,
} from "@/components/commission/rateRows";
import {
  BookCurrencyLine,
  CommissionAccrualNote,
  CommissionCurrencyLabel,
} from "@/components/commission/CommissionBookLabel";
import {
  CommissionPolicyFilterBar,
  filterContradictionNote,
} from "@/components/commission/CommissionPolicyFilters";
import {
  COMMISSION_BOOKS,
  bookMatchesFilters,
  commissionAccrual,
  currencyAccrual,
  parseCommissionBook,
  parseCommissionCurrency,
  visibleCurrencies,
  type CommissionPolicyFilters,
} from "@/components/commission/commissionCurrency";
import type { CurrencyCode } from "@/lib/currency";
import type { CommissionMode } from "@/lib/queries";
import {
  commissionConfigKeys,
  fetchAdminCommissionDefaults,
  ratesAreUniform,
} from "@/lib/queries";
import { fmtRelative } from "@/lib/format";
import { Perm } from "@/lib/perms";
import { useMyPerms } from "@/hooks/useMyPerms";
import { useAdminDirectory } from "@/hooks/useAdminNames";
import { REPORT_CURRENCY_DEFAULT } from "@/components/currency/ReportCurrencySelect";

/** Which level of the chain is on screen. */
type Scope = "markets" | "events" | "admins";

const SCOPES: Scope[] = ["markets", "events", "admins"];

interface Search {
  scope?: Scope;
  /** The trading book — and so the currency — the page is narrowed to. */
  book?: CommissionMode;
  currency?: CurrencyCode;
}

export const Route = createFileRoute("/_app/treasury/revenue/policies")({
  // Every param is read through a parser that answers "no filter" to
  // anything it cannot represent in the controls: a URL the pickers
  // could not show would leave the list narrowed under a bar reading
  // "All books".
  validateSearch: (s: Record<string, unknown>): Search => ({
    scope: SCOPES.includes(s.scope as Scope) ? (s.scope as Scope) : undefined,
    book: parseCommissionBook(s.book),
    currency: parseCommissionCurrency(s.currency),
  }),
  component: CommissionPoliciesPage,
});

const ZERO = { maker: 0, taker: 0, redeem: 0 } as const;

function CommissionPoliciesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.CommissionView);
  const canConfig = perms.has(Perm.CommissionConfig);

  // Markets lead: an override there is the decision that outranks the
  // rest, and it is the one an operator arrives holding a specific poll
  // in mind. Defaults are where you go when nothing more specific
  // applies, which is also where they sit in the chain.
  const scope: Scope = search.scope ?? "markets";
  // Merged, never replaced: a scope pick that dropped the book and
  // currency would silently widen what the operator is looking at, and
  // the tab is the one thing on this page that is NOT a filter.
  const setScope = (next: Scope) =>
    void navigate({
      search: (prev) => ({ ...prev, scope: next === "markets" ? undefined : next }),
    });
  const filters: CommissionPolicyFilters = {
    book: search.book,
    currency: search.currency,
  };
  const setFilters = (next: CommissionPolicyFilters) =>
    void navigate({
      search: (prev) => ({ ...prev, book: next.book, currency: next.currency }),
    });

  if (perms.loaded && !canView) {
    return (
      <EmptyState
        title="Commission policies"
        description="Reading commission rates needs the COMMISSION_VIEW permission."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/treasury/revenue/commissions"
          search={{ currency: REPORT_CURRENCY_DEFAULT }}
          className="text-xs text-accent hover:underline"
        >
          ← Commissions
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">
          Commission policies
        </h1>
        <p className="max-w-[85ch] text-xs text-muted">
          {/* A literal apostrophe, not &rsquo;: the runtime bridge matches
              translations against the DOM text, and an entity in the
              extracted phrase can never match what the browser renders. */}
          What share of each collected fee an admin earns on the polls they
          author, and where that share is decided. A market override beats an
          event override, which beats the author’s default. Every rate here is a
          share of a fee the platform has already collected, never an extra
          charge to a trader.
        </p>
      </div>

      <Card>
        <CardHeader className="flex-wrap gap-3">
          <CardTitle>Where the rate is decided</CardTitle>
          <SegmentedControl<Scope>
            options={[
              { value: "markets", label: "Markets" },
              { value: "events", label: "Events" },
              { value: "admins", label: "Admin defaults" },
            ]}
            value={scope}
            onChange={setScope}
          />
        </CardHeader>
        <CardBody className="space-y-5">
          <CommissionPolicyFilterBar filters={filters} onChange={setFilters} />

          {/* Nothing is fetched until the viewer's permissions are known:
              a rate must not be downloaded — even briefly, even to be
              thrown away — by someone who may turn out not to hold
              COMMISSION_VIEW. */}
          {!perms.loaded ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <>
              {scope === "markets" && (
                <MarketPolicies canConfig={canConfig} filters={filters} />
              )}
              {scope === "events" && (
                <EventPolicies canConfig={canConfig} filters={filters} />
              )}
              {scope === "admins" && (
                <AdminDefaults canConfig={canConfig} filters={filters} />
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * The bottom of the chain: every admin, in every currency, with the
 * three independent rates SM-387 gave them.
 *
 * This is the list that used to be a card on the Commissions report. It
 * moved here whole — same join, same dialog, same "Not set" state — so
 * the report can be a report.
 *
 * The currency filter DOES narrow these rows (SM-522). It narrows them
 * to the policies in that currency, which is a real subset now: an admin
 * with a Gold rate and no Red one appears under Gold as configured and
 * under Red as Not set, and both statements are true of the same person.
 * The book filter narrows them too, via the currencies that book
 * collects — a Cash-only view is a Gold view, because Gold is the only
 * currency Cash fees are collected in.
 */
function AdminDefaults({
  canConfig,
  filters,
}: {
  canConfig: boolean;
  filters: CommissionPolicyFilters;
}) {
  const directory = useAdminDirectory();

  // Which currencies the rows may be in, after both filters. Derived
  // from the books rather than from the currency filter alone, so
  // "Game book" narrows to Red and Green without the operator having to
  // know which currencies that book collects.
  const currencies = useMemo(() => visibleCurrencies(filters), [filters]);

  // Fetched unfiltered and narrowed here. The filter is a currency SET
  // (a book can select two), and the endpoint takes one currency — so
  // asking per currency would be up to three requests to build one list
  // whose total row count the page states.
  const q = useQuery({
    queryKey: commissionConfigKeys.adminDefaults(),
    queryFn: () => fetchAdminCommissionDefaults(),
  });

  const rows = useMemo(
    () =>
      buildRateRows(
        directory.admins,
        q.data?.admin_defaults ?? [],
        directory.labels,
        currencies,
      ),
    [directory.admins, directory.labels, q.data, currencies],
  );

  const [needle, setNeedle] = useState("");
  const shown = useMemo(() => filterRows(rows, needle), [rows, needle]);
  // Held by KEY — the (admin, currency) pair — not by value: a background
  // refetch may replace the row object while the dialog is open, and an
  // admin id alone would now match three rows and open the wrong policy.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const editing =
    rows.find((r) => rateRowKey(r.admin_id, r.currency) === editingKey) ?? null;

  const configured = shown.filter((r) => r.rates !== null);
  const unset = shown.filter((r) => r.rates === null);
  const admins = new Set(rows.map((r) => r.admin_id)).size;
  const mixed = configured.filter((r) => r.rates && !ratesAreUniform(r.rates));
  const loading = q.isLoading || directory.loading;

  // A plain wrapper, not a <section>: the two groups below are the
  // sections, and nesting one inside another would make "the section
  // headed 'No rates set'" ambiguous to anything selecting by role.
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xs font-semibold text-fg">
            Admin defaults
            <span className="ml-2 font-normal tabular-nums text-muted">
              {rows.length}
            </span>
          </h2>
          {!loading && !q.isError && rows.length > 0 && (
            // Policies, not admins: the count has to name what it counts
            // now that one admin contributes up to three rows, or "3 of
            // 9 configured" reads as a third of the operators rather
            // than a third of the policies.
            <p className="text-2xs tabular-nums text-muted">
              {/* One whole sentence in ONE text node: the content bridge
                  extracts text nodes, and a sentence split around a JSX
                  expression becomes a fragment no translation can match. */}
              {`${rows.filter((r) => r.rates !== null).length} of ${rows.length} policies configured across ${admins} ${
                admins === 1 ? "recipient" : "recipients"
              }`}
              {mixed.length > 0 ? ` · ${mixed.length} mixed` : ""}
            </p>
          )}
        </div>
        <Input
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          placeholder="Search by name, email or id"
          aria-label="Search admins"
          className="h-8 w-64 text-xs"
        />
      </div>

      <p className="max-w-[85ch] text-2xs leading-relaxed text-muted">
        Recipients earn a separate share of each fee type — maker, taker and
        redeem — in each currency, and the two are independent: one row here is
        one currency’s policy, and editing it leaves every other currency
        exactly as it is. Commission is paid out of the platform revenue the fee
        landed in, so a currency with no policy earns nothing rather than
        falling back to another one. These defaults are the bottom of the
        chain: any override above them replaces all three fee types at once.
        Editing a rate here never re-prices a live market — terms are frozen
        when a market first publishes.
      </p>

      <DefaultsCurrencyPanel filters={filters} />

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : q.isError ? (
        <LoadFailed
          what="commission policies"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No recipients"
          description="No admin is listed and none has a commission rate configured, so no market inherits one."
        />
      ) : shown.length === 0 ? (
        <p className="text-xs text-muted">
          No admin matches &ldquo;{needle}&rdquo;.
        </p>
      ) : (
        <div className="space-y-5">
          {/* Needs a decision first: the rows an operator is here to act
              on, rather than buried alphabetically among the ones already
              settled. */}
          {unset.length > 0 && (
            <PolicyGroup
              title="No rates set"
              // Deliberately avoids the word "configured": it is the
              // title of the OTHER group, and Playwright's text matching
              // is case-insensitive, so the hint made every locator for
              // "the Configured section" ambiguous.
              hint="Nothing has been decided for this recipient in this currency, so a fee collected in it earns them nothing. Not the same as a deliberate 0%, and not filled in by another currency."
              rows={unset}
              canConfig={canConfig}
              onEdit={setEditingKey}
            />
          )}
          {configured.length > 0 && (
            <PolicyGroup
              title="Configured"
              hint="Rates apply to markets published from now on — anything already live keeps the rates it launched with."
              rows={configured}
              canConfig={canConfig}
              onEdit={setEditingKey}
            />
          )}
        </div>
      )}

      {!loading && !q.isError && !directory.available && (
        <CommissionNotice tone="muted">
          Only admins with stored rates are listed — showing everyone needs the{" "}
          <span className="font-mono">ADMIN_VIEW</span> permission.
        </CommissionNotice>
      )}

      {!canConfig && (
        <CommissionNotice tone="muted">
          Read-only — changing a commission rate needs the{" "}
          <span className="font-mono">COMMISSION_CONFIG</span> permission.
        </CommissionNotice>
      )}

      {editing && (
        <AdjustCommissionDialog
          adminId={editing.admin_id}
          adminLabel={editing.label}
          currency={editing.currency}
          current={editing.rates ?? ZERO}
          configured={editing.rates !== null}
          configVersion={editing.config_version}
          title={editing.rates === null ? "Set commission" : "Adjust commission"}
          onClose={() => setEditingKey(null)}
        />
      )}
    </div>
  );
}

/**
 * Where an admin default is actually earned, under the current filters.
 *
 * The rows below answer WHICH currency each policy is; this panel answers
 * what that currency does — which book collects it, and whether a
 * commission earned in it can be paid out. Both halves are needed: a Red
 * policy accrues real money into a real account with no withdrawal rail,
 * and a row showing "20%" says nothing about that.
 */
function DefaultsCurrencyPanel({
  filters,
}: {
  filters: CommissionPolicyFilters;
}) {
  const books = COMMISSION_BOOKS.filter((m) => bookMatchesFilters(m, filters));
  const contradiction = filterContradictionNote(filters);
  // Warned about only when the filter has narrowed to books that all pay
  // nothing. Unfiltered, the Cash book pays and the per-book lines below
  // already carry the Game caveat — a page-level warning there would
  // overstate it.
  // Warned about only when every book left by the filter has no payout
  // rail. Unfiltered, the Cash book pays out and the per-book lines below
  // already carry the caveat — a page-level warning there would overstate
  // it.
  const silent = books.length > 0 && books.every((m) => !commissionAccrual(m).payable);

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-line-1 bg-bg-2 px-3 py-2.5">
      <p className="text-2xs font-semibold uppercase tracking-wide text-t-3">
        Where these rates are earned
      </p>
      <p className="max-w-[85ch] text-2xs leading-relaxed text-muted">
        Recipients are configured separately in each currency, and a fee is
        collected in the currency of the book it was charged on. So the rows
        below are one policy per currency, and this is where each of those
        currencies is earned — and whether what accrues in it can be paid out.
      </p>
      {contradiction ? (
        <p className="text-2xs leading-relaxed text-muted">{contradiction}</p>
      ) : (
        <div className="space-y-1.5">
          {books.map((mode) => (
            <BookCurrencyLine key={mode} mode={mode} />
          ))}
        </div>
      )}
      {silent && <CommissionAccrualNote mode={books[0]} />}
    </div>
  );
}

function PolicyGroup({
  title,
  hint,
  rows,
  canConfig,
  onEdit,
}: {
  title: string;
  hint: string;
  rows: RateRow[];
  canConfig: boolean;
  /** Takes the (admin, currency) key — an admin id matches three rows. */
  onEdit: (rowKey: string) => void;
}) {
  return (
    <section>
      <div className="mb-2">
        <h3 className="text-xs font-semibold text-fg">
          {title}
          <span className="ml-2 font-normal tabular-nums text-muted">
            {rows.length}
          </span>
        </h3>
        <p className="text-2xs text-muted">{hint}</p>
      </div>
      {/* The column header exists so the three values below it are
          readable as maker / taker / redeem without hovering. It hides
          with the triple it labels. */}
      <div className="hidden items-center gap-3 border-b border-border px-3 pb-1 lg:flex">
        <span className="min-w-0 flex-1 text-2xs uppercase tracking-wide text-muted">
          Recipient
        </span>
        <span className="w-28 text-2xs uppercase tracking-wide text-muted">
          Currency
        </span>
        <span className="w-[19rem] text-right text-2xs uppercase tracking-wide text-muted">
          {FEE_TYPE_ORDER.map((k) => FEE_TYPE_LABEL[k]).join(" · ")}
        </span>
        <span className="w-24 text-right text-2xs uppercase tracking-wide text-muted">
          Updated
        </span>
        <span className="w-16" />
      </div>
      <ul className="divide-y divide-border rounded-b-lg border border-t-0 border-border">
        {rows.map((r) => (
          <PolicyRow
            key={rateRowKey(r.admin_id, r.currency)}
            row={r}
            canConfig={canConfig}
            onEdit={() => onEdit(rateRowKey(r.admin_id, r.currency))}
          />
        ))}
      </ul>
    </section>
  );
}

const BLOCK_NOTE: Record<NonNullable<RateRow["block"]>, string> = {
  locked: "This account is suspended — restore it to change the rates.",
  unlisted: "These rates belong to an admin who is no longer in the directory.",
};

const BLOCK_BADGE: Record<NonNullable<RateRow["block"]>, string> = {
  locked: "Suspended",
  unlisted: "Not listed",
};

function PolicyRow({
  row,
  canConfig,
  onEdit,
}: {
  row: RateRow;
  canConfig: boolean;
  onEdit: () => void;
}) {
  const set = row.rates !== null;
  const rates = row.rates ?? ZERO;

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-sm text-fg"
          title={`${row.label} — ${row.admin_id}`}
        >
          {row.label}
          {row.block && (
            <Badge tone="warning" className="ml-2" title={BLOCK_NOTE[row.block]}>
              {BLOCK_BADGE[row.block]}
            </Badge>
          )}
        </p>
        <p className="truncate text-2xs text-muted">
          {row.email ?? row.admin_id}
        </p>
      </div>

      {/* The currency is the row's identity, so it sits beside the name
          rather than in a tooltip: two rows for one recipient differ ONLY
          here, and a reader who cannot see which is which cannot use the
          list at all. */}
      <div className="flex w-28 items-center gap-1.5">
        <CommissionCurrencyLabel code={row.currency} />
        {!currencyAccrual(row.currency).payable && (
          <span
            className="text-2xs text-warn"
            title={currencyAccrual(row.currency).reason}
          >
            ⚠
          </span>
        )}
      </div>

      <div className="flex w-[19rem] max-w-full justify-end">
        <CommissionRateTriple rates={rates} configured={set} />
      </div>

      <div className="hidden w-24 text-right text-2xs text-muted lg:block">
        {set && row.updated_at ? fmtRelative(row.updated_at) : "—"}
      </div>

      <div className="w-16 text-right">
        {canConfig && !row.block && (
          <Button size="sm" variant="ghost" onClick={onEdit}>
            {set ? "Change" : "Set"}
          </Button>
        )}
      </div>
    </li>
  );
}
